import React, { useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Camera } from 'lucide-react';
import { 
  Carousel, 
  CarouselContent, 
  CarouselItem, 
  Image,
  Button,
  type CarouselApi 
} from '@city-sage/aurum-avis-labs-components';
import { Image as ImageType } from '@city-sage/core-client';
import { Avatar } from './Avatar';
import { LikeButton } from './actions/LikeButton';
import { useReactions } from '../lib/useReactions';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';

// Post data interface for post-related images
interface PostData {
  id: string;
  account: {
    id: string;
    userName: string;
    firstName?: string;
    lastName?: string;
    avatarUrl?: string;
  };
  createdAt: Date;
  text?: string;
}

interface ImageGalleryProps {
  imageUrls: string[];
  images?: (ImageType | undefined)[];
  titles?: (string | undefined)[];
  postData?: (PostData | undefined)[];
  aspectRatio?: string;
  className?: string;
  showCounter?: boolean;
  showDots?: boolean;
  showArrows?: boolean;
}

export function ImageGallery({ 
  imageUrls,
  images = [],
  titles = [],
  postData = [],
  aspectRatio = "aspect-[4/3]",
  className = "",
  showCounter = true,
  showDots = true,
  showArrows = true
}: ImageGalleryProps) {
  const { t } = useTranslation();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  React.useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  const scrollTo = useCallback((index: number) => {
    api?.scrollTo(index);
  }, [api]);

  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  // Component for displaying post image overlay
  const PostImageOverlay = ({ post, index }: { post: PostData; index: number }) => {
    const timeAgo = formatDistanceToNow(post.createdAt, { addSuffix: true });

    return (
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent pt-16 pb-8 px-4">
        {/* User Info */}
        <div className="flex items-center gap-3 mb-3">
          <Avatar 
            className="h-8 w-8 border-2 border-white/20" 
            account={post.account as any}
          />
          <div className="flex-1 min-w-0 pr-20">
            <p className="text-white font-medium text-sm truncate">
              {post.account.userName || 'Anonymous'}
            </p>
            <p className="text-white/70 text-xs">{timeAgo}</p>
          </div>
        </div>

        {/* Post Text */}
        {post.text && (
          <div className="pr-20">
            <p className="text-white text-sm line-clamp-2 leading-relaxed">
              {post.text}
            </p>
          </div>
        )}
      </div>
    );
  };

  // Component for displaying like button on post images
  const PostLikeButton = ({ post }: { post: PostData }) => {
    const { isLiked, likeCount, toggleLike, isPending } = useReactions({
      reactableType: 'post',
      reactableId: post.id
    });

    return (
      <div className="absolute bottom-8 right-4 z-20">
        <div onClick={(e) => e.stopPropagation()}>
          <LikeButton
            isLiked={isLiked}
            likeCount={likeCount}
            onToggleLike={toggleLike}
            isPending={isPending}
            variant="dark"
            layout="horizontal"
            size="sm"
          />
        </div>
      </div>
    );
  };

  if (imageUrls.length === 0) {
    return (
      <div className={`${aspectRatio} w-full bg-gray-200 flex items-center justify-center ${className}`}>
        <div className="text-center text-gray-500">
          <Camera className="h-12 w-12 mx-auto mb-2" />
          <p>{t('itinerary.stopPage.noImagesAvailable')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <Carousel
        setApi={setApi}
        className="w-full"
        opts={{
          align: "start",
          loop: true,
        }}
      >
        <CarouselContent>
          {imageUrls.map((imageUrl, index) => {
            const image = images[index] || undefined;
            const title = titles[index];
            const post = postData[index] || undefined;

            return (
              <CarouselItem key={`image-${index}`}>
                <div className={`relative ${aspectRatio} w-full bg-gray-200`}>
                  <Image
                    src={imageUrl}
                    alt={title || `Image ${index + 1}`}
                    className="object-cover w-full h-full"
                    imageClassName="h-full"
                    image={image}
                  />

                  {/* Post Image Overlay */}
                  {post && <PostImageOverlay post={post} index={index} />}

                  {/* Post Like Button */}
                  {post && <PostLikeButton post={post} />}

                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>

        {/* Navigation arrows */}
        {showArrows && imageUrls.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white hover:bg-black/70 h-8 w-8 rounded-full z-10"
              onClick={scrollPrev}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white hover:bg-black/70 h-8 w-8 rounded-full z-10"
              onClick={scrollNext}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </>
        )}
      </Carousel>

      {/* Image counter */}
      {showCounter && imageUrls.length > 1 && (
        <div className="absolute top-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm backdrop-blur-sm z-10">
          {current} / {count}
        </div>
      )}

      {/* Dot indicators */}
      {showDots && imageUrls.length > 1 && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
          {imageUrls.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollTo(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === current - 1 ? "bg-white" : "bg-white/50"
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
} 